import { Box, Flex, Image, Link, Paragraph, PlainLink, Truncated } from '@customerio/pluma-components/react';
import logoMint from '@/assets/images/logos/logo-mint.svg';
import defaultAccountLogo from '@/assets/images/default-account-logo.svg';
import * as styles from './auth-form-layout.css';

/** Who is signed in, for the pages that charge or cancel an account. */
export type MinimalAppHeaderAccount = {
  /** The signed-in user's full name. */
  userName: string;
  /** The account name, falling back to the user's email when the account has no name. */
  accountNameOrEmail: string;
};

type MinimalAppHeaderProps = {
  /**
   * Show the "Don't have an account? Start your free trial." callout — the
   * login index only (Ember's `isLoginIndexRoute`). Signup still lives in
   * Ember, so the link is a plain `/signup` navigation.
   */
  showSignupCallout?: boolean;
  /**
   * Identifies the signed-in user and account. Ember's `@showAccount`, which the checkout and
   * account-canceled pages passed. Those pages have no nav chrome, so without this the user cannot
   * tell which account is about to be charged — worth knowing before entering a card.
   *
   * Passed in rather than read from the auth service: this component lives in `components/`, which
   * must stay presentational and cannot import `services/`.
   */
  account?: MinimalAppHeaderAccount;
};

/**
 * Stripped-down header (logo, plus the signup callout on the login index) shown
 * on the unauthenticated pages. Ported from the Ember `MinimalAppHeader`.
 */
export function MinimalAppHeader({ showSignupCallout = false, account }: MinimalAppHeaderProps) {
  return (
    <Box
      className={styles.loginHeader}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      backgroundColor="invert-bold"
      px="150"
    >
      <PlainLink href="https://customer.io" data-test-login-signup-logo-link>
        <Image alt="Customer.io" src={logoMint} width="129" height="18" verticalAlign="bottom" />
      </PlainLink>

      {account != null ? (
        <Flex alignItems="flex-start" className={styles.account} data-test-minimal-header-account>
          <Image alt="Default Account" src={defaultAccountLogo} height="31" />

          <Box ml="150" maxW="full" minW="0">
            {/*
              Ember set no color here, which lands Text's dark default on this dark `invert-bold`
              header. Matching the sibling callout's `color="white"` instead — the point of showing
              the account is that the user can read which one is about to be charged.
            */}
            <Truncated color="white" tooltip={account.userName}>
              {account.userName}
            </Truncated>
            <Truncated color="white" tooltip={account.accountNameOrEmail}>
              {account.accountNameOrEmail}
            </Truncated>
          </Box>
        </Flex>
      ) : null}

      {showSignupCallout ? (
        <Flex>
          <Paragraph color="white">
            Don't have an account?{' '}
            <Link
              href="/signup"
              variant="secondary"
              isUnderlined
              className={styles.signupLink}
              data-test-login-signup-link
            >
              Start your free trial
            </Link>
            .
          </Paragraph>
        </Flex>
      ) : null}
    </Box>
  );
}
